/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { SEText } from "../../../../components/SEText";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  HabilitarDiagnosticoInterface,
  UfInterface,
} from "../../../../interfaces";
import { HabilitarDiagnosticoService } from "../services";
import useRenderFormStore from "../../stores/useRenderFormStore";
import { BaSeDialogContext, BaSeIcon } from "@base/react";
import useFormMutation from "../../hooks/useFormMutation";
import theme from "../../../../mui-theme";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { RiSearchLine } from "react-icons/ri";
import SEFilterSelect from "../../../../components/SEFilterSelect";
import { useQuery } from "react-query";
import { sortArrayByProp } from "../../../../utils/utils";
import Spinner from "react-spinner-material";
import { FormInputRadio } from "../../../../../components/StyledComponents";

const CustomIcon = styled(RiSearchLine)(({ theme }) => ({
  fill: theme.palette.primary.main,
  height: 26,
  width: 26,
  top: "calc(50% - .75em) !important",
}));

interface FilterOptions {
  tipo: { value: string; label: string }[];
  ufs: UfInterface[];
}

const FormularioHabilitarDiagnostico = () => {
  const { showModal } = React.useContext(BaSeDialogContext);
  const history = useHistory();
  const [notFoundError, setNotFoundError] = useState<string | null>(null);

  const { data: filterOptions } = useQuery<FilterOptions>({
    queryFn: () => HabilitarDiagnosticoService.getOptions(false),
    queryKey: ["getOptionsHabilitarDiagnostico"],
  });

  const { handleShowForm } = useRenderFormStore();

  const { control, handleSubmit, reset, setValue, watch, clearErrors } =
    useForm<HabilitarDiagnosticoInterface>({
      defaultValues: {
        uf: undefined,
      },
    });

  const { fields, replace } = useFieldArray({
    control,
    name: "diagnosticosArray",
  });

  const uf = watch("uf");

  const { refetch: refetchDiagnostico, isLoading: isLoadingDiagnostico } =
    useQuery<any>({
      queryFn: () => HabilitarDiagnosticoService.getDiagnosticosSas(uf?.sigla!),
      queryKey: ["getDiagnosticosSasHabilitarDiagnostico"],
      enabled: Boolean(uf?.sigla),
      onSuccess: (response) => {
        if (!response?.length) {
          replace([]);
          setNotFoundError(
            "Não foram encontrados diagnósticos disponíveis para esta UF."
          );
          return;
        }
        replace(response);
        clearErrors("uf");
      },
      onError: (e: any) => {
        replace([]);
        if (e?.response?.data?.descricao?.includes("Chave Inválida")) {
          setNotFoundError(
            "Não existe token de acesso cadastrado para esta UF. Atualize o token."
          );
          return;
        }
        setNotFoundError(
          "Não foram encontrados diagnósticos disponíveis para esta UF."
        );
      },
    });

  const diagnosticos = watch("diagnosticosArray");

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "uf") {
      }
      return value;
    });
    return () => subscription.unsubscribe();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const onResetFields = () => {
    reset();
  };

  const goBack = () => {
    handleShowForm(false);
    history.push(`/tabelas-de-controle/habilitar-no-app`);
  };

  const { createMutation, createMutationLoading } = useFormMutation({
    showModal,
    service: HabilitarDiagnosticoService,
    onCloseModalSuccess: () => {
      onResetFields();
      goBack();
    },
    onCloseModalUpdate: () => goBack(),
  });

  const submitForm = async (dataForm: HabilitarDiagnosticoInterface) => {
    const diagnosticosParaIncluir = dataForm?.diagnosticosArray
      .filter((d: any) => d?.checked)
      .map((diagnostico: any) => {
        const { checked, ...rest } = diagnostico;
        return rest;
      });

    if (!diagnosticosParaIncluir?.length) return;
    await createMutation(diagnosticosParaIncluir);
  };

  const marcarOpcao = (index: number) => {
    const options = [...diagnosticos];
    const optionSelected = options[index];

    options[index].checked = !optionSelected?.checked;
    setValue("diagnosticosArray", options);
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
        }}
      >
        <Grid container>
          <Grid container>
            <Grid item xs={12}>
              <SEText
                as="h2"
                size={"medium"}
                font="Mulish"
                weight="bold"
                color="#7B80FF"
              >
                Incluir
              </SEText>
            </Grid>
          </Grid>

          <Grid container marginY={3} spacing={2} alignItems="flex-end">
            <Grid item md={3}>
              <Controller
                control={control}
                name="uf"
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <SEFilterSelect
                    id={"uf"}
                    defaultOption={"Selecione uma das opções"}
                    IconComponent={CustomIcon}
                    label="UF *"
                    value={value}
                    onChange={onChange}
                    options={sortArrayByProp(filterOptions?.ufs, "sigla")}
                    displayField="sigla"
                    error={error}
                  />
                )}
              />
            </Grid>
            <Grid marginBottom={0.5} marginLeft={1}>
              <IconButton
                onClick={() => {
                  setNotFoundError(null);
                  refetchDiagnostico();
                }}
                disabled={!Boolean(uf?.sigla)}
                type="button"
                aria-label="search"
              >
                <RiSearchLine
                  color={!Boolean(uf?.sigla) ? "#9ca2a8" : "#9999FF"}
                  size={22}
                />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>

        {isLoadingDiagnostico || createMutationLoading ? (
          <Grid container item alignItems={"center"} justifyContent={"center"}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Spinner
                radius={80}
                color={"#9999FF"}
                stroke={2}
                visible={true}
              />
              <p>Carregando...</p>
            </div>
          </Grid>
        ) : (
          <>
            {!notFoundError && !!diagnosticos?.length && (
              <Grid container alignItems="center">
                <Grid container item alignItems={"center"} mb={4}>
                  <p
                    style={{
                      color: "#7B80FF",
                      fontStyle: "bold",
                      fontWeight: "600",
                      fontSize: "12px",
                    }}
                  >
                    Diagnósticos disponíveis
                  </p>
                </Grid>
                {fields?.map((f: any, index) => (
                  <Grid
                    container
                    key={index}
                    alignItems="center"
                    gap={2}
                    marginBottom={2}
                  >
                    <Grid item md={3}>
                      <Controller
                        control={control}
                        name={`diagnosticosArray.${index}.idDiagnostico`}
                        rules={{ required: "Este campo é obrigatório" }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <>
                            <TextField
                              id={`diagnosticosArray.${index}.idDiagnostico`}
                              label="ID"
                              disabled
                              fullWidth
                              value={value ?? ""}
                              onChange={onChange}
                            />
                            {error && (
                              <p style={{ color: "red", marginTop: "8px" }}>
                                {error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </Grid>
                    <Grid item md={3}>
                      <Controller
                        control={control}
                        name={`diagnosticosArray.${index}.nome`}
                        rules={{ required: "Este campo é obrigatório" }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <>
                            <TextField
                              id={`diagnosticosArray.${index}.nome`}
                              label="ID"
                              disabled
                              fullWidth
                              value={value ?? ""}
                              onChange={onChange}
                            />
                            {error && (
                              <p style={{ color: "red", marginTop: "8px" }}>
                                {error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </Grid>
                    <Grid item alignItems="center">
                      <FormInputRadio
                        type="radio"
                        checked={f?.checked}
                        onClick={() => marcarOpcao(index)}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}

            {!!notFoundError && !diagnosticos?.length && (
              <Grid container>
                <div
                  style={{
                    height: "40px",
                    display: "flex",
                    flexDirection: "row",
                    backgroundColor: "rgba(196, 196, 196, 0.32)",
                    alignItems: "center",
                    padding: "3px 7px",
                  }}
                >
                  <>
                    <BaSeIcon
                      name="exclamation-circle"
                      size={20}
                      color="#FF0000"
                      description={"description"}
                    />
                    <p style={{ marginLeft: "4px" }}>{notFoundError}</p>
                  </>
                </div>
              </Grid>
            )}
          </>
        )}

        <Grid
          item
          md={12}
          display="flex"
          justifyContent="flex-end"
          gap={2}
          marginTop={2}
          width="100%"
        >
          <Button
            style={{
              borderColor: "#7B80FF",
              color: "#7B80FF",
              fontFamily: "Mulish, sans-serif",
              fontSize: "12px",
            }}
            variant="outlined"
            onClick={goBack}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            style={{
              backgroundColor: "#7B80FF",
              fontFamily: "Mulish, sans-serif",
              fontSize: "12px",
            }}
            onClick={handleSubmit(submitForm)}
            disabled={!Boolean(uf?.sigla)}
          >
            Incluir
          </Button>
        </Grid>
      </Paper>
    </ThemeProvider>
  );
};

export default FormularioHabilitarDiagnostico;
